import { jwtDecode } from 'jwt-decode'
import type { StytchJWTPayload } from '@/types/stytch'
import { getStytchCookieDomain } from '@/utilities/getDomain'
import {
  STYTCH_SESSION_JWT_COOKIE,
  STYTCH_SESSION_OPAQUE_COOKIE,
} from '@/utilities/stytch/constants'
import { getExpiredStytchCookies } from '@/utilities/stytch/sessionCookies'

function readCookie(name: string): string {
  if (typeof document === 'undefined') return ''
  return (
    document.cookie
      .split('; ')
      .map((cookiePart) => cookiePart.split('='))
      .find(([cookieName]) => cookieName === name)?.[1]
      ?.trim() ?? ''
  )
}

/**
 * Reads and decodes the Stytch session JWT from browser cookies.
 */
export function getStytchJWT(): StytchJWTPayload | null {
  try {
    const sessionJWT = readCookie(STYTCH_SESSION_JWT_COOKIE)
    if (!sessionJWT) {
      return null
    }

    return jwtDecode<StytchJWTPayload>(sessionJWT)
  } catch {
    return null
  }
}

/**
 * Reads the browser Stytch session cookies used by `session.updateSession`.
 * The opaque session token cookie is `stytch_session`.
 */
export function getStytchSessionTokens(): { sessionJwt: string; sessionToken: string } | null {
  const sessionJwt = readCookie(STYTCH_SESSION_JWT_COOKIE)
  const sessionToken = readCookie(STYTCH_SESSION_OPAQUE_COOKIE)
  if (!sessionJwt || !sessionToken) return null
  return { sessionJwt, sessionToken }
}

/**
 * Deletes every cookie whose name contains "stytch". Expires both the host-only and the
 * `Domain=` form, because the SDK writes cookies with a Domain and a host-only cookie can coexist.
 */
export function clearStytchCookies(): void {
  if (typeof document === 'undefined') return
  const cookieDomain = getStytchCookieDomain()
  const domain = cookieDomain === 'localhost' ? undefined : cookieDomain
  const cookies = document.cookie.split(';')

  cookies.forEach((cookie) => {
    const cookieParts = cookie.split('=')
    const cookieName = cookieParts[0]?.trim()

    if (cookieName?.toLowerCase().includes('stytch')) {
      for (const expired of getExpiredStytchCookies(cookieName, domain)) {
        document.cookie = expired
      }
    }
  })
}

/**
 * Returns true if the Stytch session JWT's local expiry (`exp`) has passed.
 * Note: Stytch JWTs have a short local cache window (~5 min). An expired JWT
 * does not necessarily mean the server-side session is invalid — the client
 * SDK will obtain a fresh JWT on the next authenticated request.
 * Returns true (expired) when the token is malformed or missing.
 */
export function isSessionJwtExpired(sessionJwt: string): boolean {
  try {
    const { exp } = jwtDecode<StytchJWTPayload>(sessionJwt)
    return Date.now() >= exp * 1000
  } catch {
    return true
  }
}
